import type { SessionContext } from "../../lib/sessionContext"
import type { SessionStatus } from "../../lib/sessionEvents"
import { ContextMeter } from "./ContextMeter"

// The slim line above the composer: how full the conversation is, what it has
// cost, and whether the agent is working right now. The model, effort and
// agent pickers moved into the composer card itself — they are properties of
// the message about to be sent, and that is where the desktop app puts them.

interface StatusBarProps {
  context: SessionContext | undefined
  status: SessionStatus | undefined
  /** Already formatted by cloud — this app never turns micro-USD into a string itself. */
  cost: string | undefined
  /** The workspace's company, which pays for this session — the web twin of
   *  the CLI sidebar's "Billed to". Read-only: switching is a new session. */
  billedTo: string | undefined
  contextSelected: boolean
  onShowContext: () => void
}

export function StatusBar({ context, status, cost, billedTo, contextSelected, onShowContext }: StatusBarProps) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-wrap items-center gap-x-2 gap-y-1 px-4 pt-2 text-xs text-muted">
      <ContextMeter context={context} onClick={onShowContext} expanded={contextSelected} />

      {cost && (
        <span
          className="font-mono tabular-nums"
          title={`Charged to ${billedTo ?? "the workspace's company"} for this session, at its rate. Recorded by the gateway after each turn, so it lags the reply by a moment.`}
        >
          {cost}
        </span>
      )}

      {billedTo && (
        <span className="truncate" title="This workspace belongs to this company, so its inference is billed there.">
          Billed to {billedTo}
        </span>
      )}

      <div className="ml-auto flex items-center gap-2">
        <StatusIndicator status={status} />
      </div>
    </div>
  )
}

// Busy and retrying are the same wait to the machine and a completely different
// wait to the person doing it. Before session.status was consumed, a rate-limit
// backoff and a healthy turn showed the identical spinner forever, with the
// reason sitting unread on the event bus.
function StatusIndicator({ status }: { status: SessionStatus | undefined }) {
  if (!status || status.type === "idle") return null

  if (status.type === "retry") {
    return (
      <span className="flex items-center gap-2 text-warning" role="status">
        <span className="size-2 animate-pulse rounded-full bg-warning" aria-hidden />
        <span className="truncate" title={status.message}>
          Retrying{status.attempt ? ` (attempt ${status.attempt})` : ""}
          {status.message ? ` — ${status.message}` : ""}
        </span>
      </span>
    )
  }

  return (
    <span className="flex items-center gap-2" role="status">
      <span className="size-3 animate-spin rounded-full border-2 border-border border-t-accent" aria-hidden />
      Working…
    </span>
  )
}
